import { useEffect, useState } from 'react';
import driverApi from '../../clients/DriverClient';
import { MatchDriverResponse } from '../../models/MatchDriverResponse';

export type VenueDriverState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'linked'; driver: MatchDriverResponse }
  /** No venue driver has the account's email, or the token has none: staff link it at the front desk. */
  | { status: 'unlinked' }
  | { status: 'error' };

/**
 * The venue driver behind the logged-in account, on a brand whose race data comes from its venue's
 * API. The venue owns the link (`Drivers.UserId`), so the account's own `driverId` (a World of Racing
 * driver) is never used: `driver/me`, and on a first visit `driver/link` to claim the driver by email.
 */
export function useVenueDriver(enabled: boolean, userId: string | undefined): VenueDriverState {
  const [state, setState] = useState<VenueDriverState>({ status: enabled && userId ? 'loading' : 'idle' });

  useEffect(() => {
    if (!enabled || !userId) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      try {
        const mine = await driverApi.getMyDriver();
        if (mine) {
          if (!cancelled) setState({ status: 'linked', driver: mine });
          return;
        }
        const link = await driverApi.linkMyDriver();
        if (cancelled) return;
        setState(link.status === 'linked' ? { status: 'linked', driver: link.driver } : { status: 'unlinked' });
      } catch (e) {
        console.error('Error loading your venue driver:', e);
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, userId]);

  return state;
}
